import React, {
  Suspense,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import SearchBox from "./SearchBox"; // Make sure this path is correct
import usePins from "../../hooks/usePins";

const CardSelectorModal = ({
  isOpen,
  onClose,
  onSelect,
  items,
  CardComponent,
  title,
  highlightVersions = [],
  pinNamespace,
  getId,
}) => {
  const modalContentRef = useRef(null);
  const { sortPinnedFirst } = usePins(pinNamespace);
  const orderedItems = pinNamespace
    ? sortPinnedFirst(items, getId ?? ((item) => item?.id ?? item?.key))
    : items;
  const [searchTerm, setSearchTerm] = useState("");
  const [isRegexMode, setIsRegexMode] = useState(() => {
    return localStorage.getItem("search-regex-mode") === "true";
  });

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handleWheel = useCallback((e) => {
    const { current: content } = modalContentRef;
    if (content) {
      const { scrollTop, scrollHeight, clientHeight } = content;
      if (
        (scrollTop === 0 && e.deltaY < 0) ||
        (scrollTop + clientHeight === scrollHeight && e.deltaY > 0)
      ) {
        e.preventDefault();
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, handleKeyDown]);

  const handleSearch = (term, regexMode) => {
    setSearchTerm(term);
    setIsRegexMode(regexMode);
  };

  const filteredItems = orderedItems.filter((item) =>
    Object.values(item).some((value) => {
      const val = (
        (typeof value === "object" ? JSON.stringify(value) : value) ?? ""
      ).toString();
      if (isRegexMode) {
        try {
          const regex = new RegExp(searchTerm, "i");
          return regex.test(val);
        } catch (e) {
          // If the regex is invalid, fall back to normal search
          return val.toLowerCase().includes(searchTerm.toLowerCase());
        }
      } else {
        return val.toLowerCase().includes(searchTerm.toLowerCase());
      }
    })
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 p-6 rounded-3xl w-full max-w-6xl h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          <div className="w-1/2 max-w-md">
            <SearchBox
              value={searchTerm}
              onSearch={handleSearch}
              placeholder="Filter ..."
            />
          </div>
        </div>
        <div
          ref={modalContentRef}
          className="overflow-y-auto flex-grow dark-slim-scrollbar"
          onWheel={handleWheel}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => (
              <div key={item.id || item.key}>
                <CardComponent
                  className="cursor-pointer hover:bg-yellow-500/10 p-2 rounded-lg"
                  item={item}
                  onClick={() => onSelect(item)}
                  highlightVersions={highlightVersions}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardSelectorModal;
